import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import API_URL from '../config';
import logger from '../utils/logger';
import Mixpanel from '../utils/mixpanel';
import InvitationHandler from './InvitationHandler';

const Login = () => {
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [isServerAvailable, setIsServerAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [invitationCode, setInvitationCode] = useState(null);
  const [showInvitation, setShowInvitation] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    checkServerHealth();
    
    // Track login page view
    Mixpanel.track('View Login Page', {
      feature: 'Authentication',
      action: 'View Login Page',
      origin: location.origin,
      pathname: location.pathname,
      referrer: document.referrer || 'none'
    });
    
    // Parse URL query parameters once
    const queryParams = new URLSearchParams(location.search);
    
    // Check for invitation code in URL
    const invitation = queryParams.get('invitation');
    if (invitation) {
      setInvitationCode(invitation);
      setShowInvitation(true);
      
      // Track invitation detection
      Mixpanel.track('Invitation Detected', {
        feature: 'Team Management',
        action: 'Invitation Code Detected',
        page: 'Login'
      });
    }
    
    // Check for Google auth callback
    const error = queryParams.get('error');
    
    logger.debug('🔍 Login: URL query params check', {
      hasError: !!error,
      errorType: error || 'none',
      fullQueryString: location.search
    });
    
    if (error) {
      if (error === 'google_auth_failed') {
        setError('Google authentication failed. Please try again or use email login.');
      } else if (error === 'no_user') {
        setError('No user found with this Google account. Please register first.');
      }
    }
    
    // Handle token from Google auth callback
    const token = localStorage.getItem('googleAuthToken');
    const userId = localStorage.getItem('googleAuthUserId');
    
    logger.debug('🔍 Login: Google auth token check', {
      hasGoogleToken: !!token,
      hasGoogleUserId: !!userId
    });
    
    if (token && userId) {
      localStorage.removeItem('googleAuthToken');
      localStorage.removeItem('googleAuthUserId');
      
      // Set the actual auth token and user ID
      localStorage.setItem('token', token);
      localStorage.setItem('userId', userId);
      
      logger.debug('🔍 Login: Google auth successful, navigating to dashboard');
      // Navigate to dashboard
      navigate('/dashboard');
    }
  }, [navigate, location]);

  useEffect(() => {
    // Check if user has a remembered login
    const rememberedToken = localStorage.getItem('rememberedToken');
    const rememberedUserId = localStorage.getItem('rememberedUserId');
    
    logger.debug('🔍 Login: Initial component load', { 
      hasRememberedToken: !!rememberedToken,
      hasRememberedUserId: !!rememberedUserId,
      currentPath: location.pathname,
      referrer: document.referrer || 'none'
    });
    
    if (rememberedToken && rememberedUserId) {
      // If remembered credentials exist, set them as active and navigate to dashboard
      localStorage.setItem('token', rememberedToken);
      localStorage.setItem('userId', rememberedUserId);
      logger.debug('🔍 Login: Using remembered credentials, navigating to dashboard');
      navigate('/dashboard');
    }
    
    // IMPORTANT: Don't clear tokens for direct navigation to the site
    // This allows users to remain logged in when visiting directly
    const isDirectNavigation = !document.referrer || 
                               document.referrer.indexOf(window.location.host) === -1;
    
    // Only clear tokens if not coming from home AND not a direct navigation
    const fromHome = localStorage.getItem('fromHome');
    logger.debug('🔍 Login: Token clearing check', {
      isDirectNavigation,
      fromHome: !!fromHome
    });
    
    if (!fromHome && !isDirectNavigation) {
      // Clear tokens only for internal navigation that's not from home
      logger.debug('🔍 Login: Clearing tokens due to internal navigation');
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
    } else if (fromHome) {
      localStorage.removeItem('fromHome');
    }
  }, []);

  const checkServerHealth = async () => {
    try {
      logger.info('Checking server health');
      const response = await axios.get(`${API_URL}/api/health`, {
        timeout: 15000 // 15 seconds timeout
      });
      
      if (response.data && response.data.status === 'OK') {
        setIsServerAvailable(true);
        
        // Check database connection status
        if (response.data.database === 'connected') {
          logger.info('Server and database are healthy');
        } else if (response.data.database === 'disconnected') {
          logger.warn('Server is healthy but database is disconnected');
          // Don't set an error here as the database might still be connecting
          // and will be available by the time the user tries to log in
        }
      } else {
        setIsServerAvailable(false);
        setError('Server is currently experiencing issues. Please try again later.');
        logger.warn('Server health check failed', response.data);
      }
    } catch (error) {
      setIsServerAvailable(false);
      setError('Cannot connect to server. Please check your internet connection or try again later.');
      logger.error('Server health check error', error);
    }
    logger.info('Health check completed');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setError('');
      setIsLoading(true);
      
      logger.debug('🔍 Login: Attempting login', { 
        email: credentials.email,
        hasPassword: !!credentials.password,
        rememberMe,
        hasInvitationCode: !!invitationCode
      });
      
      // Validate input
      if (!credentials.email || !credentials.password) {
        logger.warn('Login attempt with missing credentials');
        setError('Please enter both email and password');
        setIsLoading(false);
        return;
      }
      
      // Log network conditions
      try {
        const connectionInfo = {
          online: navigator.onLine,
          connectionType: navigator.connection ? navigator.connection.effectiveType : 'unknown',
          rtt: navigator.connection ? navigator.connection.rtt : 'unknown'
        };
        logger.debug('🔍 Login: Network conditions', connectionInfo);
      } catch (err) {
        logger.debug('🔍 Login: Could not get network info', { error: err.message });
      }
      
      // Attempt login with increased timeout
      logger.debug('🔍 Login: Sending request to API', {
        url: `${API_URL}/api/users/login`,
        withCredentials: true
      });
      
      const startTime = Date.now();
      
      try {
        const response = await axios.post(
          `${API_URL}/api/users/login`,
          credentials,
          { 
            withCredentials: true,
            timeout: 60000 // Increase timeout to 60 seconds
          }
        );
        
        const endTime = Date.now();
        
        logger.debug('🔍 Login: Response received', {
          status: response.status,
          responseTime: `${endTime - startTime}ms`,
          hasData: !!response.data,
          dataKeys: Object.keys(response.data || {})
        });
        
        if (response.data && response.data.token) {
          logger.debug('🔍 Login: Valid token received');
          
          // Store token and user info in localStorage - this is what PrivateRoute checks
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('userId', response.data._id || response.data.userId);
          
          // Log what was stored to help debug
          logger.debug('🔍 Login: Token storage complete', {
            hasToken: !!localStorage.getItem('token'),
            hasUserId: !!localStorage.getItem('userId'),
            tokenPrefix: localStorage.getItem('token') ? localStorage.getItem('token').substring(0, 10) + '...' : 'none'
          });
          
          // Track successful login
          Mixpanel.track('User Login', {
            feature: 'Authentication',
            action: 'Login',
            status: 'Success',
            method: 'Email',
            hasInvitation: !!invitationCode
          });
          
          // Fetch user profile to determine role before redirecting
          logger.debug('🔍 Login: Fetching user profile to determine role');
          
          try {
            const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
            const profileResponse = await axios.get(`${baseUrl}/api/users/info`, {
              headers: {
                Authorization: `Bearer ${response.data.token}`
              },
              timeout: 8000
            });
            
            logger.debug('🔍 Login: User profile data:', JSON.stringify(profileResponse.data));
            
            // Check if the user is a coach based on profile data
            const userProfile = profileResponse.data;
            
            // Since the role field might not be in the database,
            // check the user's name for "coach" as a fallback
            const isCoach = 
              // First check if there's a role field
              (userProfile.role && 
               typeof userProfile.role === 'string' && 
               userProfile.role.toLowerCase() === 'coach') ||
              
              // Then check if the name contains "coach" (case insensitive)
              (userProfile.name && 
               typeof userProfile.name === 'string' && 
               userProfile.name.toLowerCase().includes('coach'));
            
            logger.debug('🔍 Login: Is user a coach?', isCoach);
            
            if (isCoach) {
              logger.debug('🔍 Login: User is a coach, redirecting to coach dashboard');
              window.location.href = '/coach-dashboard';
            } else {
              logger.debug('🔍 Login: User is not a coach, redirecting to dashboard');
              // If there's an invitation code, include it in the redirect
              if (invitationCode) {
                window.location.href = `/dashboard?invitation=${invitationCode}`;
              } else {
                window.location.href = '/dashboard';
              }
            }
          } catch (profileError) {
            logger.warn('🔍 Login: Error fetching user profile, defaulting to dashboard', profileError);
            // If there's an invitation code, include it in the redirect
            if (invitationCode) {
              window.location.href = `/dashboard?invitation=${invitationCode}`;
            } else {
              window.location.href = '/dashboard';
            }
          }
        } else {
          logger.warn('🔍 Login: Response missing token', response.data);
          setError('Invalid response from server. Please try again.');
          setIsLoading(false);
        }
      } catch (error) {
        const endTime = Date.now();
        logger.error('🔍 Login: Error during login request', {
          message: error.message,
          responseTime: `${endTime - startTime}ms`,
          status: error.response?.status,
          data: error.response?.data
        });
        
        // Provide user-friendly error messages based on the error type
        if (error.response) {
          // Server responded with an error status
          if (error.response.status === 401) {
            setError('Invalid email or password. Please try again.');
          } else if (error.response.status === 429) {
            setError('Too many login attempts. Please try again later.');
          } else {
            setError(`Server error: ${error.response.data?.message || 'Unknown error'}`);
          }
        } else if (error.request) {
          // Request was made but no response received
          if (error.message.includes('timeout')) {
            setError('Server is taking too long to respond. Please try again later.');
          } else {
            setError('No response from server. Please check your internet connection.');
          }
        } else {
          // Error setting up the request
          setError('Error connecting to server. Please try again.');
        }
        
        // Track failed login
        Mixpanel.track('User Login', {
          feature: 'Authentication',
          action: 'Login',
          status: 'Failed',
          method: 'Email',
          error: error.message
        });
        
        setIsLoading(false);
      }
    } catch (error) {
      logger.error('🔍 Login: Unexpected error', error);
      setError('An unexpected error occurred. Please try again.');
      setIsLoading(false);
      
      // Track error
      Mixpanel.track('User Login', {
        feature: 'Authentication',
        action: 'Login',
        status: 'Error',
        method: 'Email',
        error: error.message
      });
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'rememberMe') {
      setRememberMe(checked);
    } else {
      setCredentials(prev => ({ ...prev, [name]: value }));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-base-200 px-4 py-8">
      {/* Show invitation information if there's an invitation code */}
      {showInvitation && (
        <div className="fixed top-0 left-0 right-0 bg-base-100 shadow-md p-4 z-10">
          <InvitationHandler onlyVerify={true} />
        </div>
      )}
      
      <div className="w-full max-w-md bg-transparent">
        <div className="px-5 sm:px-8">
          <div className="flex flex-col items-center mb-6">
            <img
              src="/images/mental-edge-coach-logo.png"
              alt="Mental Edge Coach"
              className="w-28 mb-2"
              onError={e => { e.target.onerror = null; e.target.style.display = 'none'; }}
            />
            <h2 className="text-2xl font-bold text-primary">Sign In</h2>
          </div>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="form-control">
              <label className="label" htmlFor="email">
                <span className="label-text">Email</span>
              </label>
              <input
                id="email"
                name="email"
                type="email"
                className="input input-bordered w-full"
                autoComplete="email"
                required
                value={credentials.email}
                onChange={handleChange}
                autoFocus
              />
            </div>
            <div className="form-control">
              <label className="label" htmlFor="password">
                <span className="label-text">Password</span>
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  className="input input-bordered w-full pr-12"
                  autoComplete="current-password"
                  required
                  value={credentials.password}
                  onChange={handleChange}
                  style={{ paddingRight: '2.5rem' }}
                />
                <button 
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center px-2 text-gray-500 hover:text-gray-700 focus:outline-none"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex="-1"
                  style={{ height: '100%', right: '0', top: '0', bottom: '0', backgroundColor: 'transparent' }}
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between mt-2">
              <div className="form-control">
                <label className="cursor-pointer label justify-start gap-2">
                  <input 
                    type="checkbox" 
                    name="rememberMe"
                    className="checkbox checkbox-primary checkbox-sm aspect-square" 
                    checked={rememberMe}
                    onChange={handleChange}
                    style={{ width: '1rem', height: '1rem' }}
                  />
                  <span className="label-text">Remember me</span>
                </label>
              </div>
              <Link to="/forgot-password" className="text-primary underline text-sm font-medium">
                Forgot password?
              </Link>
            </div>
            {error && (
              <div className="text-center mb-4">
                <p className="text-red-500 font-medium">{error}</p>
              </div>
            )}
            <button
              type="submit"
              className="btn btn-primary w-full mt-2"
              disabled={!isServerAvailable || isLoading}
            >
              {isLoading ? (
                <>
                  <span className="loading loading-spinner loading-xs mr-2"></span>
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
            <div className="text-center mt-4">
              <span className="text-sm text-base-content">
                New to Mental Edge Coach?{' '}
                <Link to="/new-profile" className="text-primary font-bold underline">Create an account</Link>
              </span>
            </div>

            {/* Privacy and Terms Links */}
            <div className="text-center mt-4">
              <span className="text-xs text-gray-600">
                <Link to="/terms-and-conditions" className="text-blue-500 hover:text-blue-700" target="_blank" rel="noopener noreferrer">Terms & Conditions</Link>
                {' • '}
                <Link to="/privacy-policy" className="text-blue-500 hover:text-blue-700" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
              </span>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;